/**
 * Player service
 * Handles all player-related API calls
 */

import { API_ENDPOINTS } from './endpoints';
import { fetchApi } from '@/lib/utils/api';
import type { Player, PublicPlayer } from '@/lib/types/auction';
import type { PaginatedResponse } from '@/lib/types/api';
import type {
  Enrollment,
  CricketDetails,
  FootballDetails,
} from '@/lib/types/enrollment';
import { showSuccessToast } from '@/lib/utils/toast';

export interface CreatePlayerInput {
  player: {
    firstName: string;
    lastName: string;
    email?: string;
    profilePicture?: string;
  };
  role: Player['role'];
  basePrice?: number;
  poolId: string;
  tournamentId: string;
}

interface UpdatePlayerInput extends Partial<CreatePlayerInput> {
  status?: Player['status'];
  teamId?: string;
}

interface UpdateProfileInput {
  firstName: string;
  lastName: string;
  profilePicture?: string;
  tournamentId: string;
}

interface IconAssignmentInput {
  teamId: string;
  amount: number;
  tournamentId: string;
  playerId: string;
  userId?: string;
}

interface IconRemoveInput {
  tournamentId: string;
  playerId: string;
  userId?: string;
}

interface SearchParam {
  query?: string;
  role?: Player['role'];
  poolId?: string;
  page?: number;
  limit?: number;
  status?: Player['status'];
}

interface EnrollmentRequestInput {
  tournamentId: string;
  sportDetails?: {
    cricket?: CricketDetails;
    football?: FootballDetails;
  };
}

export const playerService = {
  /**
   * Create new player
   */
  async create(tournamentId: string, data: CreatePlayerInput): Promise<Player> {
    const response = await fetchApi<Player>(API_ENDPOINTS.PLAYERS.BASE, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        tournamentId,
      }),
    });
    showSuccessToast('player', 'create');
    return response.result;
  },

  /**
   * Update player
   */
  async update(id: string, data: UpdatePlayerInput): Promise<Player> {
    const response = await fetchApi<Player>(API_ENDPOINTS.PLAYERS.PROFILE(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    showSuccessToast('player', 'update');
    return response.result;
  },

  /**
   * Update unverified player profile
   */
  async updateProfile(id: string, data: UpdateProfileInput): Promise<Player> {
    const response = await fetchApi<Player>(API_ENDPOINTS.PLAYERS.PROFILE(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    showSuccessToast('player', 'update');
    return response.result;
  },

  /**
   * Delete player
   */
  async delete(id: string): Promise<void> {
    await fetchApi<void>(API_ENDPOINTS.PLAYERS.PROFILE(id), {
      method: 'DELETE',
    });
    showSuccessToast('player', 'delete');
  },

  /**
   * Get player by ID
   */
  async getById(id: string): Promise<Player> {
    const response = await fetchApi<Player>(API_ENDPOINTS.PLAYERS.PROFILE(id), {
      requireAuth: false,
    });
    return response.result;
  },

  /**
   * Search players
   */
  async search(params: SearchParam): Promise<PaginatedResponse<Player>> {
    const searchParams = this.generateSearchParams(params);
    const response = await fetchApi<PaginatedResponse<Player>>(
      API_ENDPOINTS.PLAYERS.SEARCH(searchParams.toString())
    );
    return response.result;
  },

  /**
   * Tournament players
   */
  async getPlayersOfTournament(
    tournamentId: string,
    params: SearchParam
  ): Promise<PaginatedResponse<Player>> {
    const searchParams = this.generateSearchParams(params);
    const response = await fetchApi<PaginatedResponse<Player>>(
      API_ENDPOINTS.PLAYERS.TOURNAMENT(tournamentId, searchParams.toString()),
      { requireAuth: false }
    );
    return response.result;
  },

  generateSearchParams(params: SearchParam) {
    const searchParams = new URLSearchParams();
    if (params.query) searchParams.set('q', params.query);
    if (params.role) searchParams.set('role', params.role);
    if (params.poolId) searchParams.set('poolId', params.poolId);
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.status) searchParams.set('status', params.status);
    return searchParams;
  },

  /**
   * Bulk upload players
   */
  async bulkUpload(formData: FormData): Promise<{ message: string }> {
    const response = await fetchApi<{ message: string }>(
      API_ENDPOINTS.PLAYERS.BULK_UPLOAD,
      {
        method: 'POST',
        body: formData,
      }
    );
    return response.result;
  },

  /**
   * Assign player as icon to a team
   */
  async assignToTeam(data: IconAssignmentInput): Promise<Player> {
    const response = await fetchApi<Player>(API_ENDPOINTS.AUCTIONS.PRESALE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    showSuccessToast('player', 'update');
    return response.result;
  },

  /**
   * Remove team assignment
   */
  async removeFromTeam(data: IconRemoveInput): Promise<Player> {
    const response = await fetchApi<Player>(
      API_ENDPOINTS.AUCTIONS.REVERT_PRESALE,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    showSuccessToast('player', 'update');
    return response.result;
  },

  /**
   * Request enrollment as a player in a tournament with sport-specific data
   */
  async requestEnrollment(data: EnrollmentRequestInput): Promise<Enrollment> {
    const response = await fetchApi<Enrollment>(
      API_ENDPOINTS.PLAYERS.REQUEST_ENROLLMENT,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    showSuccessToast('enrollment', 'request');
    return response.result;
  },

  /**
   * Approve player enrollment with pool assignment
   * This allows player-specific validation rules to be applied
   */
  async approveEnrollment(
    enrollmentId: string,
    poolId: string
  ): Promise<Player> {
    const response = await fetchApi<Player>(
      API_ENDPOINTS.PLAYERS.APPROVE_ENROLLMENT(enrollmentId),
      {
        method: 'POST',
        body: JSON.stringify({ poolId }),
      }
    );
    showSuccessToast('enrollment', 'approve');
    return response.result;
  },

  /**
   * Reject player enrollment
   * This allows player-specific validation rules to be applied
   */
  async rejectEnrollment(
    enrollmentId: string,
    reason?: string
  ): Promise<Player> {
    const response = await fetchApi<Player>(
      API_ENDPOINTS.PLAYERS.REJECT_ENROLLMENT(enrollmentId),
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    );
    showSuccessToast('enrollment', 'reject');
    return response.result;
  },

  /**
   * Get public players sitemap
   */
  async getPublicPlayersSitemap(): Promise<PublicPlayer[]> {
    const response = await fetchApi<{ result: PublicPlayer[] }>(
      API_ENDPOINTS.PLAYERS.SITEMAP,
      { requireAuth: false }
    );
    return Array.isArray(response.result) ? response.result : [];
  },
};
